import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
// import ReactMarkdown from "react-markdown";
import MCQViewer from "./MCQViewer";
import TrueFalseViewer from "./TrueFalseViewer";
import MatchViewer from "./MatchViewer";
import FillUpsViewer from "./FillUpsViewer";
import AnalysisComponent from "./AnalysisComponent";
import MDEditor from "@uiw/react-md-editor";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import Switch from "@mui/material/Switch";
import ContextPopup from "./ContextPopup";
import SuccessPopup from "./SuccessPopup";
import {
  updatePageForCourse,
  updatePageForSubject,
  updatePageForTopic,
  updatePageForSubtopic,
  getPageForCourse,
  getPageForSubject,
  getPageForTopic,
  getPageForSubtopic,
} from "../../helper/apicalls/course";
import {
  getMaterials,
  getMaterialsByTitleKey,
} from "../../helper/apicalls/materials";
import {
  GENERATE_CONTENT_URL,
  GENERATE_CONTENT_NEW_FORMAT_URL,
  GENERATE_CONTENT_TEACHER_URL,
} from "../../helper/apiurls/apiurls";
import { insertContentWithSequenceManagement } from "./contentSequenceUtils";
import {
  getPrompts,
  getPromptById,
  updatePrompt,
} from "../../helper/apicalls/prompts";
import "../../styles/common/contentGeneratorPopup.scss";
import "../../styles/common/popup.scss";
import Cookies from "js-cookie";

const ContentGeneratorPopup = ({
  open,
  handleClose,
  onSave,
  existingContent = "",
  pageId,
  pageData,
  context,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  insertionIndex,
  isReplaceMode = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [prompt, setPrompt] = useState("");
  const token = Cookies.get("teacher_token") || Cookies.get("institution_token") || Cookies.get("admin_token") || "";

  const [systemPrompts, setSystemPrompts] = useState({});
  const [systemPromptsLoading, setSystemPromptsLoading] = useState(false);
  const [systemPromptsError, setSystemPromptsError] = useState("");
  const [systemPromptSaving, setSystemPromptSaving] = useState({});
  const [systemPromptSaveStatus, setSystemPromptSaveStatus] = useState({});
  const [contentType, setContentType] = useState("trueorfalse");
  const [isPromptEditing, setIsPromptEditing] = useState({});
  const [originalSystemPrompts, setOriginalSystemPrompts] = useState({});

  useEffect(() => {
    if (!open) return;
    let active = true;

    const fetchDefaultPromptForType = async (type) => {
      try {
        const list = await getPrompts(type);
        const defaultPromptInfo = list.find((p) => p.default === true) || list[0];
        if (defaultPromptInfo && defaultPromptInfo.id) {
          const fullPrompt = await getPromptById(defaultPromptInfo.id);
          return fullPrompt;
        }
      } catch (err) {
        console.error(`Error fetching prompt details for ${type}:`, err);
      }
      return null;
    };

    const loadSystemPrompts = async () => {
      if (active) {
        setSystemPromptsLoading(true);
        setSystemPromptsError("");
      }
      try {
        const pt = pageData?.page_type;
        const promptsToFetch = [];

        if (pt === "content" || pt === "active_recall_content") {
          promptsToFetch.push("content");
        } else if (pt === "test" || pt === "active_recall_test") {
          promptsToFetch.push("mcq", "trueorfalse", "match", "fillups");
        } else {
          promptsToFetch.push(contentType);
        }

        const fetched = {};
        await Promise.all(
          promptsToFetch.map(async (t) => {
            try {
              const fullPrompt = await fetchDefaultPromptForType(t);
              if (fullPrompt && active) {
                fetched[t] = fullPrompt;
              }
            } catch (err) {
              console.error(`Error fetching prompt for type ${t}:`, err);
            }
          })
        );
        if (active) {
          setSystemPrompts(fetched);
          setOriginalSystemPrompts(JSON.parse(JSON.stringify(fetched)));
        }
      } catch (err) {
        if (active) {
          setSystemPromptsError("Failed to load system prompts");
        }
        console.error(err);
      } finally {
        if (active) {
          setSystemPromptsLoading(false);
        }
      }
    };

    loadSystemPrompts();

    return () => {
      active = false;
    };
  }, [open, pageData?.page_type, contentType]);

  const handleSystemPromptTextChange = (type, value) => {
    setSystemPrompts((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        prompt: value,
      },
    }));
  };

  const handleSaveSystemPrompt = async (type) => {
    const promptObj = systemPrompts[type];
    if (!promptObj || !promptObj.id) return;

    setSystemPromptSaving((prev) => ({ ...prev, [type]: true }));
    setSystemPromptSaveStatus((prev) => ({ ...prev, [type]: "" }));
    try {
      await updatePrompt(promptObj.id, {
        prompt: promptObj.prompt,
      });
      setIsPromptEditing((prev) => ({ ...prev, [type]: false }));
      setOriginalSystemPrompts((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          prompt: promptObj.prompt,
        },
      }));
      setSystemPromptSaveStatus((prev) => ({ ...prev, [type]: "Saved successfully!" }));
      setTimeout(() => {
        setSystemPromptSaveStatus((prev) => ({ ...prev, [type]: "" }));
      }, 3000);
    } catch (err) {
      console.error(`Error updating system prompt for type ${type}:`, err);
      setSystemPromptSaveStatus((prev) => ({ ...prev, [type]: "Failed to save" }));
    } finally {
      setSystemPromptSaving((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleCancelSystemPromptEdit = (type) => {
    if (originalSystemPrompts[type]) {
      setSystemPrompts((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          prompt: originalSystemPrompts[type].prompt,
        },
      }));
    }
    setIsPromptEditing((prev) => ({ ...prev, [type]: false }));
  };

  const [generatedContent, setGeneratedContent] = useState("");
  const [manualContent, setManualContent] = useState("");
  const [categories, setCategories] = useState("");
  const [websocketStatus, setWebsocketStatus] = useState("");
  const [manualMCQContent, setManualMCQContent] = useState({
    questions: [
      {
        question: "",
        options: ["", "", "", ""],
        correctIndex: 0,
        explanation: "",
        hint: "",
      },
    ],
  });
  const [manualTrueFalseContent, setManualTrueFalseContent] = useState({
    questions: [
      {
        question: "",
        answer: "true",
        explanation: "",
        hint: "",
      },
    ],
  });

  const [manualMatchContent, setManualMatchContent] = useState({
    pairs: [
      {
        left: "",
        right: "",
      },
    ],
  });

  const [manualFillUpsContent, setManualFillUpsContent] = useState({
    sentences: [
      {
        sentence: "",
        answers: [""],
        explanation: "",
        hint: "",
      },
    ],
  });

  const [manualAnalysisContent, setManualAnalysisContent] = useState({
    title: "",
    content: "",
    hint: "",
  });

  const normalizeAnswer = (answer) => {
    if (typeof answer === "boolean") {
      return answer ? "true" : "false";
    }
    if (typeof answer === "string") {
      const lowerAnswer = answer.toLowerCase();
      if (
        lowerAnswer === "true" ||
        lowerAnswer === "1" ||
        lowerAnswer === "a" ||
        lowerAnswer === "yes"
      ) {
        return "true";
      }
      if (
        lowerAnswer === "false" ||
        lowerAnswer === "0" ||
        lowerAnswer === "b" ||
        lowerAnswer === "no"
      ) {
        return "false";
      }
    }
    return answer;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const testQuestionTypes = [
    { key: "mcq", label: "MCQ" },
    { key: "trueorfalse", label: "True or False" },
    { key: "match", label: "Match" },
    { key: "fillups", label: "Fill Ups" },
    // { key: "analysis", label: "Analysis" },
  ];

  const [testSwitchState, setTestSwitchState] = useState(
    testQuestionTypes.reduce((acc, item) => {
      acc[item.key] = false;
      return acc;
    }, {}),
  );

  const [testQuestionCounts, setTestQuestionCounts] = useState({});

  const websocketRef = useRef(null);

  const [isContextOpen, setIsContextOpen] = useState(false);
  const [contextMaterials, setContextMaterials] = useState([]);
  const [contextLoading, setContextLoading] = useState(false);
  const [contextError, setContextError] = useState("");
  const [contextExtract, setContextExtract] = useState(null);
  const [contextMaterialId, setContextMaterialId] = useState(null);
  const [contextPageSelections, setContextPageSelections] = useState({});
  const [contextSuccessOpen, setContextSuccessOpen] = useState(false);

  const normalizeContextToString = (value) => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  };

  const normalizeMarkdownValue = (value) => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch (_) {
      return String(value);
    }
  };

  const mdPreviewOptions = {
    remarkPlugins: [remarkMath],
    rehypePlugins: [rehypeKatex],
  };

  useEffect(() => {
    if (open) {
      setPrompt("");
      setGeneratedContent("");
      setManualContent("");
      setCategories("");
      setManualMCQContent({
        questions: [
          {
            question: "",
            options: ["", "", "", ""],
            correctIndex: 0,
            explanation: "",
            hint: "",
          },
        ],
      });
      setManualTrueFalseContent({
        questions: [
          {
            question: "",
            answer: "true",
            explanation: "",
            hint: "",
          },
        ],
      });
      setManualMatchContent({
        pairs: [
          {
            left: "",
            right: "",
          },
        ],
      });
      setManualFillUpsContent({
        sentences: [
          {
            sentence: "",
            answers: [""],
            explanation: "",
            hint: "",
          },
        ],
      });
      setManualAnalysisContent({
        title: "",
        content: "",
        hint: "",
      });
      setContextExtract(null);
      setContextMaterialId(null);
      setError("");
      setIsLoading(false);
      setActiveTab(0);

      if (pageData && pageData.page_type) {
        const pt = pageData.page_type;

        if (
          ["mcq", "match", "fillups", "trueorfalse", "analysis"].includes(pt)
        ) {
          setContentType(pt);
        } else {
          switch (pt) {
            case "quiz":
              setContentType("mcq");
              break;
            case "trueorfalse":
              setContentType("trueorfalse");
              break;
            case "content":
              setContentType("content");
              break;
            case "active_recall_content":
              setContentType("content");
              break;
            default:
              setContentType("trueorfalse");
          }
        }
      } else {
        setContentType("trueorfalse");
      }

      setTestSwitchState(
        testQuestionTypes.reduce((acc, item) => {
          acc[item.key] = false;
          return acc;
        }, {}),
      );
      setTestQuestionCounts({});
    }
  }, [open, pageData]);

  useEffect(() => {
    if (pageData && pageData.page_type && open) {
      const pt = pageData.page_type;

      if (["mcq", "match", "fillups", "trueorfalse", "analysis"].includes(pt)) {
        setContentType(pt);
      } else {
        switch (pt) {
          case "quiz":
            setContentType("mcq");
            break;
          case "trueorfalse":
            setContentType("trueorfalse");
            break;
          case "content":
            setContentType("trueorfalse");
            break;
          case "active_recall_content":
            setContentType("content");
            break;
          default:
            setContentType("trueorfalse");
        }
      }
    }
  }, [pageData, open]);

  const handleGenerate = () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      if (websocketRef.current) {
        websocketRef.current.close();
      }

      let socketUrl = GENERATE_CONTENT_URL;
      if (
        pageData &&
        pageData.page_type &&
        pageData.page_type !== "content" &&
        pageData.page_type !== "active_recall_content"
      ) {
        const role = Cookies.get("role");
        if (role === "institution" || role === "teacher") {
          socketUrl = GENERATE_CONTENT_TEACHER_URL;
        } else {
          socketUrl = GENERATE_CONTENT_NEW_FORMAT_URL;
        }
      }

      websocketRef.current = new WebSocket(socketUrl);
      websocketRef.current.onopen = () => {
        console.log("WebSocket connection opened");
        let key = "";
        switch (context) {
          case "course":
            key = courseKey || "";
            break;
          case "subject":
            key = subjectKey || "";
            break;
          case "topic":
            key = topicKey || "";
            break;
          case "subtopic":
            key = subTopicKey || "";
            break;
          default:
            key = "";
        }

        const isNonContentPage =
          pageData &&
          pageData.page_type &&
          pageData.page_type !== "content" &&
          pageData.page_type !== "active_recall_content";

        let typeToSend = "content";
        if (isNonContentPage) {
          typeToSend = contentType;
        }

        let finalPrompt = prompt.trim();

        let typesPayload = null;
        if (isNonContentPage) {
          if (pageData.page_type === "test" || pageData.page_type === "active_recall_test") {
            const typesArr = [];
            testQuestionTypes.forEach((item) => {
              const enabled = testSwitchState[item.key];
              const countNum = Number(testQuestionCounts[item.key] || 0);
              if (enabled && Number.isFinite(countNum) && countNum > 0) {
                typesArr.push({ type: item.key, count: countNum });
              }
            });
            if (typesArr.length === 0) {
              typesArr.push({ type: contentType || "mcq", count: 1 });
            }
            typesPayload = typesArr;
          } else {
            const enabled = testSwitchState[contentType];
            const countNum = Number(testQuestionCounts[contentType] || 0);
            const finalCount =
              enabled && Number.isFinite(countNum) && countNum > 0
                ? countNum
                : 1;
            typesPayload = [{ type: contentType, count: finalCount }];
          }
        }

        const contextString = contextExtract
          ? normalizeContextToString(contextExtract)
          : "";

        const message = isNonContentPage
          ? {
              prompt: finalPrompt,
              types: typesPayload || [{ type: contentType, count: 1 }],
              field: context,
              key: key,
              token: token,
              ...(contextString && {
                context: contextString,
              }),
            }
          : {
              prompt: finalPrompt,
              type: typeToSend,
              field: context,
              key: key,
              token: token,
              ...(contextString && {
                context: contextString,
              }),
            };

        websocketRef.current.send(JSON.stringify(message));
      };

      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("Parsed data:", data);
          setWebsocketStatus(data?.status);

          if (Array.isArray(data?.results)) {
            const results = data.results;

            if (pageData && (pageData.page_type === "test" || pageData.page_type === "active_recall_test")) {
              const flattenedQuestions = [];
              results.forEach((r) => {
                const structured = r?.data?.structured;
                const questions = structured?.questions;
                const content = structured?.content;

                if (Array.isArray(content) && content.length > 0) {
                  content.forEach((q) => {
                    flattenedQuestions.push(q);
                  });
                  return;
                }

                if (Array.isArray(questions) && questions.length > 0) {
                  questions.forEach((q) => {
                    flattenedQuestions.push(q);
                  });
                }
              });

              const wrapper = {
                structured: {
                  content: flattenedQuestions,
                },
              };
              setGeneratedContent(JSON.stringify(wrapper));
              setIsLoading(false);
              return;
            }

            const matchedResult =
              results.find((r) => {
                const qs = r?.data?.structured?.questions;
                const firstQ =
                  Array.isArray(qs) && qs.length > 0 ? qs[0] : null;
                return firstQ && firstQ.type === contentType;
              }) || results[0];

            const structured = matchedResult?.data?.structured;
            const questions = structured?.questions;
            const content = structured?.content;

            if (Array.isArray(questions)) {
              const contentToSet = { questions };
              setGeneratedContent(contentToSet);
              setIsLoading(false);
              return;
            }

            if (Array.isArray(content)) {
              // Store the full content array so all questions are preserved
              setGeneratedContent(content);
              setIsLoading(false);
              return;
            }

            setGeneratedContent(data);
            setIsLoading(false);
            return;
          }

          if (data.status === "completed" && data.data && data.data.content) {
            let contentToSet = data.data.content;
            try {
              if (typeof data.data.content === "string") {
                const parsedContent = JSON.parse(data.data.content);
                if (
                  parsedContent &&
                  typeof parsedContent === "object" &&
                  parsedContent.structured
                ) {
                  contentToSet = JSON.stringify(
                    parsedContent.structured,
                    null,
                    2,
                  );
                }
              }
            } catch (parseError) {
              console.warn("Could not parse structured content:", parseError);
            }
            setGeneratedContent(contentToSet);
            setIsLoading(false);
          } else if (data.type === "content" && data.content) {
            let contentToSet = data.content;
            try {
              if (typeof data.content === "string") {
                const parsedContent = JSON.parse(data.content);
                if (
                  parsedContent &&
                  typeof parsedContent === "object" &&
                  parsedContent.structured
                ) {
                  contentToSet = JSON.stringify(
                    parsedContent.structured,
                    null,
                    2,
                  );
                }
              }
            } catch (parseError) {
              console.warn("Could not parse structured content:", parseError);
            }
            setGeneratedContent(contentToSet);
            setIsLoading(false);
          } else if (data.type === "error") {
            setError(data.message || "Error generating content");
            setIsLoading(false);
          } else {
            if (typeof event.data === "string") {
              setGeneratedContent(event.data);
            } else {
              setGeneratedContent(JSON.stringify(event.data, null, 2));
            }
            setIsLoading(false);
          }
        } catch (err) {
          if (typeof event.data === "string") {
            setGeneratedContent(event.data);
          } else {
            setGeneratedContent(JSON.stringify(event.data, null, 2));
          }
          setIsLoading(false);
        }
      };

      websocketRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        setError("Failed to connect to content generation service");
        setIsLoading(false);
      };

      websocketRef.current.onclose = () => {
        console.log("WebSocket connection closed");
      };
    } catch (err) {
      setError("Failed to generate content. Please try again.");
      console.error("Error generating content:", err);
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (generatedContent) {
      try {
        let updatePageData;

        if (
          pageData &&
          (pageData.page_type === "test" ||
            pageData.page_type === "active_recall_test")
        ) {
          let fieldName = "content";
          let level;
          if (pageData?.page_type === "test") {
            level = (pageData.level || "").toLowerCase();
          }
          if (level === "medium") fieldName = "medium_content";
          else if (level === "hard") fieldName = "hard_content";

          let existingArray = [];
          if (!isReplaceMode) {
            let rawExisting = pageData ? pageData[fieldName] : undefined;
            if (
              rawExisting === undefined ||
              rawExisting === null ||
              (Array.isArray(rawExisting) && rawExisting.length === 0) ||
              rawExisting === "" ||
              rawExisting === "[]"
            ) {
              if (existingContent && existingContent !== "[]") {
                rawExisting = existingContent;
              } else if (fieldName === "medium_content" && pageData?.medium_content) {
                rawExisting = pageData.medium_content;
              } else if (fieldName === "hard_content" && pageData?.hard_content) {
                rawExisting = pageData.hard_content;
              } else if (pageData?.content) {
                rawExisting = pageData.content;
              }
            }

            if (rawExisting) {
              try {
                if (Array.isArray(rawExisting)) {
                  existingArray = rawExisting;
                } else if (typeof rawExisting === "string") {
                  const parsedExisting = JSON.parse(rawExisting);
                  if (Array.isArray(parsedExisting)) {
                    existingArray = parsedExisting;
                  } else if (parsedExisting && typeof parsedExisting === "object") {
                    existingArray = [parsedExisting];
                  }
                } else if (typeof rawExisting === "object") {
                  existingArray = [rawExisting];
                }
              } catch (e) {
                existingArray = [{ content: rawExisting }];
              }
            }
          }

          let newItems = [];
          try {
            if (
              typeof generatedContent === "string" ||
              typeof generatedContent === "object"
            ) {
              let parsed;
              if (typeof generatedContent === "string") {
                parsed = JSON.parse(generatedContent);
              } else {
                parsed = generatedContent;
              }
              if (
                parsed &&
                typeof parsed === "object" &&
                parsed.structured &&
                Array.isArray(parsed.structured.content)
              ) {
                newItems = parsed.structured.content;
              } else if (
                parsed &&
                typeof parsed === "object" &&
                Array.isArray(parsed.content)
              ) {
                newItems = parsed.content;
              } else if (
                parsed &&
                typeof parsed === "object" &&
                Array.isArray(parsed.questions)
              ) {
                newItems = parsed.questions;
              } else if (Array.isArray(parsed)) {
                newItems = parsed;
              } else if (parsed) {
                newItems = [parsed];
              }
            }
          } catch (e) {
            newItems = [];
          }

          const combinedArray = newItems.reduce((acc, item) => {
            return insertContentWithSequenceManagement(
              acc,
              item,
              insertionIndex,
              contentType,
            );
          }, existingArray);

          const isFirstGeneration =
            !existingContent ||
            existingContent === "[]" ||
            (Array.isArray(existingContent) && existingContent.length === 0);

          const contextString = contextExtract
            ? normalizeContextToString(contextExtract)
            : "";

          updatePageData = {
            id: pageId,
            [fieldName]: combinedArray,

            eval: true,

            ...(contextString && {
              context: contextString,
            }),
          };
        } else {
          let existingContentArray = [];
          if (!isReplaceMode && existingContent) {
            try {
              if (Array.isArray(existingContent)) {
                existingContentArray = existingContent;
              } else if (typeof existingContent === "string") {
                const parsed = JSON.parse(existingContent);
                if (Array.isArray(parsed)) {
                  existingContentArray = parsed;
                } else {
                  existingContentArray = [parsed];
                }
              } else if (typeof existingContent === "object") {
                existingContentArray = [existingContent];
              }
            } catch (e) {
              existingContentArray = [{ content: existingContent }];
            }
          }

          let processedGeneratedContent = generatedContent;
          const contentTypeForSave =
            pageData && pageData.page_type && pageData.page_type === "content"
              ? "content"
              : contentType;

          const isFirstGeneration =
            !existingContent ||
            existingContent === "[]" ||
            (Array.isArray(existingContent) && existingContent.length === 0);

          const contextString = contextExtract
            ? normalizeContextToString(contextExtract)
            : "";

          // Normalize generated content into a flat array of question items
          let questionItems = [];
          if (Array.isArray(processedGeneratedContent)) {
            // Full content array from structured.content — each item may have .content nested
            processedGeneratedContent.forEach((item) => {
              if (item && typeof item === "object") {
                if (Array.isArray(item.content?.questions)) {
                  // Nested questions array inside item.content
                  item.content.questions.forEach((q) => {
                    const { type: qType, sequence: _seq, hint, categories: qCat, ...contentFields } = q;
                    questionItems.push({
                      content: contentFields,
                      type: qType || item.type || contentTypeForSave,
                      ...(hint && { hint }),
                      ...(qCat || categories
                        ? { categories: qCat || categories }
                        : {}),
                    });
                  });
                } else {
                  // Item already has a content key — preserve it as-is
                  const innerContent =
                    item.content && typeof item.content === "object" && !Array.isArray(item.content)
                      ? item.content
                      : (() => {
                          const { type: _t, sequence: _s, hint: _h, categories: _c, ...rest } = item;
                          return rest;
                        })();
                  questionItems.push({
                    content: innerContent,
                    type: item.type || contentTypeForSave,
                    ...(item.hint && { hint: item.hint }),
                    ...(item.categories || categories
                      ? { categories: item.categories || categories }
                      : {}),
                  });
                }
              }
            });
          } else if (
            processedGeneratedContent &&
            typeof processedGeneratedContent === "object" &&
            Array.isArray(processedGeneratedContent.questions)
          ) {
            // { questions: [...] } shape (match, trueorfalse, etc.)
            processedGeneratedContent.questions.forEach((q) => {
              const { type: qType, sequence: _seq, hint, categories: qCat, ...contentFields } = q;
              questionItems.push({
                content: contentFields,
                type: qType || contentTypeForSave,
                ...(hint && { hint }),
                ...(qCat || categories
                  ? { categories: qCat || categories }
                  : {}),
              });
            });
          } else {
            // Single content object fallback
            questionItems = [
              {
                content: processedGeneratedContent,
                type: contentTypeForSave,
                ...(categories && { categories }),
              },
            ];
          }

          // Insert each question individually with proper sequence management
          const updatedContentArray = questionItems.reduce(
            (acc, item) =>
              insertContentWithSequenceManagement(acc, item, insertionIndex),
            existingContentArray,
          );

          updatePageData = {
            id: pageId,
            content: updatedContentArray,

            eval: true,

            ...(contextString && {
              context: contextString,
            }),
          };
        }

        let response;
        switch (context) {
          case "course":
            response = await updatePageForCourse(updatePageData);
            break;
          case "subject":
            response = await updatePageForSubject(updatePageData);
            break;
          case "topic":
            response = await updatePageForTopic(updatePageData);
            break;
          case "subtopic":
            response = await updatePageForSubtopic(updatePageData);
            break;
          default:
            throw new Error("Invalid context provided");
        }

        let updatedPageData;

        const levelForRefetch =
          pageData && pageData.page_type === "test"
            ? (pageData.level || "").toLowerCase() || undefined
            : undefined;
        switch (context) {
          case "course":
            updatedPageData = await getPageForCourse(
              courseKey,
              pageId,
              levelForRefetch,
            );
            break;
          case "subject":
            updatedPageData = await getPageForSubject(
              subjectKey,
              pageId,
              levelForRefetch,
            );
            break;
          case "topic":
            updatedPageData = await getPageForTopic(
              topicKey,
              pageId,
              levelForRefetch,
            );
            break;
          case "subtopic":
            updatedPageData = await getPageForSubtopic(
              subTopicKey,
              pageId,
              levelForRefetch,
            );
            break;
          default:
            throw new Error("Invalid context provided");
        }

        const resultWithLevel =
          levelForRefetch && updatedPageData
            ? { ...updatedPageData, level: levelForRefetch }
            : updatedPageData;

        onSave(resultWithLevel);
        handleClose();
      } catch (err) {
        setError("Failed to save content. Please try again.");
        console.error("Error saving content:", err);
      }
    }
  };

  const handleOpenContext = async () => {
    setIsContextOpen(true);
    setContextExtract(null);
    setContextLoading(true);
    setContextError("");

    try {
      let key = "";
      switch (context) {
        case "course":
          key = courseKey || "";
          break;
        case "subject":
          key = subjectKey || "";
          break;
        case "topic":
          key = topicKey || "";
          break;
        case "subtopic":
          key = subTopicKey || "";
          break;
        default:
          key = "";
      }

      const title =
        context === "subtopic"
          ? "subtopic"
          : context === "topic"
            ? "topic"
            : context === "subject"
              ? "subject"
              : context === "course"
                ? "course"
                : "";

      const data =
        title && key
          ? await getMaterialsByTitleKey({ title, key })
          : await getMaterials({ materials_type: "pdf" });
      const list = Array.isArray(data) ? data : [];
      const filtered = list.filter(
        (m) => (m?.materials_type || "").toLowerCase() === "pdf",
      );
      setContextMaterials(filtered);
    } catch (e) {
      console.error("Error loading overall materials for context:", e);
      setContextError("Unable to load materials. Please try again.");
    } finally {
      setContextLoading(false);
    }
  };

  const handleCloseContext = () => {
    setIsContextOpen(false);
  };

  const handleContextExtract = ({ materialId, pages, text }) => {
    setContextExtract(normalizeContextToString(text));
    setContextMaterialId(materialId);
    setContextPageSelections((prev) => ({ ...prev, [materialId]: pages }));
    setContextSuccessOpen(true);
  };

  const handlePromptChange = (event) => {
    setPrompt(event.target.value);
    setError("");
  };

  const handleManualContentChange = (event) => {
    setManualContent(event.target.value);
  };

  const handleAddMCQQuestion = () => {
    setManualMCQContent((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          question: "",
          options: ["", "", "", ""],
          correctIndex: 0,
          explanation: "",
        },
      ],
    }));
  };

  const handleRemoveMCQQuestion = (index) => {
    if (manualMCQContent.questions.length > 1) {
      setManualMCQContent((prev) => ({
        ...prev,
        questions: prev.questions.filter((_, i) => i !== index),
      }));
    }
  };

  const handleMCQQuestionChange = (index, field, value) => {
    setManualMCQContent((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === index ? { ...q, [field]: value } : q,
      ),
    }));
  };

  const handleMCQOptionChange = (qIndex, oIndex, value) => {
    setManualMCQContent((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === qIndex
          ? {
              ...q,
              options: q.options.map((opt, j) => (j === oIndex ? value : opt)),
            }
          : q,
      ),
    }));
  };

  const handleMCQCorrectIndexChange = (qIndex, value) => {
    setManualMCQContent((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === qIndex ? { ...q, correctIndex: parseInt(value) } : q,
      ),
    }));
  };

  const handleAddTrueFalseQuestion = () => {
    setManualTrueFalseContent((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          question: "",
          answer: "true",
          explanation: "",
        },
      ],
    }));
  };

  const handleRemoveTrueFalseQuestion = (index) => {
    if (manualTrueFalseContent.questions.length > 1) {
      setManualTrueFalseContent((prev) => ({
        ...prev,
        questions: prev.questions.filter((_, i) => i !== index),
      }));
    }
  };

  const handleTrueFalseQuestionChange = (index, field, value) => {
    setManualTrueFalseContent((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === index ? { ...q, [field]: value } : q,
      ),
    }));
  };

  const handleAddMatchPair = () => {
    setManualMatchContent((prev) => ({
      ...prev,
      pairs: [...prev.pairs, { left: "", right: "" }],
    }));
  };

  const handleRemoveMatchPair = (index) => {
    if (manualMatchContent.pairs.length > 1) {
      setManualMatchContent((prev) => ({
        ...prev,
        pairs: prev.pairs.filter((_, i) => i !== index),
      }));
    }
  };

  const handleMatchPairChange = (index, side, value) => {
    setManualMatchContent((prev) => ({
      ...prev,
      pairs: prev.pairs.map((pair, i) =>
        i === index ? { ...pair, [side]: value } : pair,
      ),
    }));
  };

  const handleAddFillUpsSentence = () => {
    setManualFillUpsContent((prev) => ({
      ...prev,
      sentences: [...prev.sentences, { sentence: "", answers: [""] }],
    }));
  };

  const handleRemoveFillUpsSentence = (index) => {
    if (manualFillUpsContent.sentences.length > 1) {
      setManualFillUpsContent((prev) => ({
        ...prev,
        sentences: prev.sentences.filter((_, i) => i !== index),
      }));
    }
  };

  const handleFillUpsSentenceChange = (index, field, value) => {
    setManualFillUpsContent((prev) => ({
      ...prev,
      sentences: prev.sentences.map((sentence, i) =>
        i === index ? { ...sentence, [field]: value } : sentence,
      ),
    }));
  };

  const handleAddFillUpsAnswer = (sentenceIndex) => {
    setManualFillUpsContent((prev) => ({
      ...prev,
      sentences: prev.sentences.map((sentence, i) =>
        i === sentenceIndex
          ? { ...sentence, answers: [...sentence.answers, ""] }
          : sentence,
      ),
    }));
  };

  const handleRemoveFillUpsAnswer = (sentenceIndex, answerIndex) => {
    setManualFillUpsContent((prev) => ({
      ...prev,
      sentences: prev.sentences.map((sentence, i) =>
        i === sentenceIndex
          ? {
              ...sentence,
              answers: sentence.answers.filter((_, j) => j !== answerIndex),
            }
          : sentence,
      ),
    }));
  };

  const handleFillUpsAnswerChange = (sentenceIndex, answerIndex, value) => {
    setManualFillUpsContent((prev) => ({
      ...prev,
      sentences: prev.sentences.map((sentence, i) =>
        i === sentenceIndex
          ? {
              ...sentence,
              answers: sentence.answers.map((answer, j) =>
                j === answerIndex ? value : answer,
              ),
            }
          : sentence,
      ),
    }));
  };

  const handleAnalysisTitleChange = (value) => {
    setManualAnalysisContent((prev) => ({
      ...prev,
      title: value,
    }));
  };

  const handleAnalysisContentChange = (value) => {
    setManualAnalysisContent((prev) => ({
      ...prev,
      content: value,
    }));
  };

  const formatManualContentForSave = () => {
    if (pageData && pageData.page_type === "content") {
      return manualContent;
    } else if (pageData && pageData.page_type !== "content") {
      switch (contentType) {
        case "mcq": {
          const formattedMCQ = (manualMCQContent.questions || []).map((q) => {
            const optionsObj = {};
            (q.options || []).forEach((option, index) => {
              const label = String.fromCharCode(65 + index);
              optionsObj[label] = option || "";
            });

            const correctLabel = String.fromCharCode(
              65 + (q.correctIndex || 0),
            );

            return {
              type: "mcq",
              question: q.question || "",
              explanation: q.explanation || "",
              hint: q.hint || "",
              answer: correctLabel,
              options: optionsObj,
              ...(categories && { categories }),
            };
          });
          return formattedMCQ;
        }

        case "trueorfalse": {
          const formattedTF = (manualTrueFalseContent.questions || []).map(
            (q) => {
              const isTrue =
                (q.answer || "true").toString().toLowerCase() === "true";
              const answerLabel = isTrue ? "A" : "B";

              return {
                type: "trueorfalse",
                question: q.question || "",
                explanation: q.explanation || "",
                hint: q.hint || "",
                answer: answerLabel,
                options: { A: "True", B: "False" },
                ...(categories && { categories }),
              };
            },
          );
          return formattedTF;
        }

        case "match": {
          const pairs = manualMatchContent.pairs || [];
          const leftValues = pairs.map((p) => p.left || "");
          const rightValues = pairs.map((p) => p.right || "");
          const formattedMatch = [
            {
              type: "match",
              pairs: [
                { key: "Engineering Concepts", values: leftValues },
                { key: "Definitions", values: rightValues },
              ],
              explanation: "",
              hint: "",
              ...(categories && { categories }),
            },
          ];
          return formattedMatch;
        }

        case "fillups": {
          const formattedFillUps = (manualFillUpsContent.sentences || []).map(
            (s) => ({
              type: "fillups",
              question: s.sentence || "",
              answer:
                (Array.isArray(s.answers) && s.answers.length > 0
                  ? s.answers[0]
                  : "") || "",
              explanation: s.explanation || "",
              hint: s.hint || "",
              ...(categories && { categories }),
            }),
          );
          return formattedFillUps;
        }

        case "analysis": {
          const formattedAnalysis = [
            {
              type: "analysis",
              question: manualAnalysisContent.title || "",
              explanation: manualAnalysisContent.content || "",
              hint: manualAnalysisContent.hint || "",
              ...(categories && { categories }),
            },
          ];
          return formattedAnalysis;
        }

        default:
          return manualContent;
      }
    }
    return manualContent;
  };

  const handleSaveManualContent = async () => {
    try {
      let contentToSave = formatManualContentForSave();
      let processedContent = contentToSave;

      console.log(processedContent, "process");

      const contentTypeForSave =
        pageData && pageData.page_type && pageData.page_type === "content"
          ? "content"
          : contentType;
      let newContentObject = null;
      let existingContentArray = [];
      if (!isReplaceMode && existingContent) {
        try {
          if (Array.isArray(existingContent)) {
            existingContentArray = existingContent;
          } else if (typeof existingContent === "string") {
            const parsed = JSON.parse(existingContent);
            if (Array.isArray(parsed)) {
              existingContentArray = parsed;
            } else {
              existingContentArray = [parsed];
            }
          } else if (typeof existingContent === "object") {
            existingContentArray = [existingContent];
          }
        } catch (parseError) {
          existingContentArray = [
            { content: existingContent, type: "content" },
          ];
        }
      }

      if (pageData && (pageData.page_type === "test" || pageData.page_type === "active_recall_test")) {
        let fieldName = "content";
        const level = (pageData.level || "").toLowerCase();
        if (level === "medium") fieldName = "medium_content";
        else if (level === "hard") fieldName = "hard_content";

        let existingArray = [];
        if (!isReplaceMode) {
          let rawExisting = pageData ? pageData[fieldName] : undefined;
          if (
            rawExisting === undefined ||
            rawExisting === null ||
            (Array.isArray(rawExisting) && rawExisting.length === 0) ||
            rawExisting === "" ||
            rawExisting === "[]"
          ) {
            if (existingContent && existingContent !== "[]") {
              rawExisting = existingContent;
            } else if (fieldName === "medium_content" && pageData?.medium_content) {
              rawExisting = pageData.medium_content;
            } else if (fieldName === "hard_content" && pageData?.hard_content) {
              rawExisting = pageData.hard_content;
            } else if (pageData?.content) {
              rawExisting = pageData.content;
            }
          }

          if (rawExisting) {
            try {
              if (Array.isArray(rawExisting)) {
                existingArray = rawExisting;
              } else if (typeof rawExisting === "string") {
                const parsedExisting = JSON.parse(rawExisting);
                if (Array.isArray(parsedExisting)) {
                  existingArray = parsedExisting;
                } else if (parsedExisting && typeof parsedExisting === "object") {
                  existingArray = [parsedExisting];
                }
              } else if (typeof rawExisting === "object") {
                existingArray = [rawExisting];
              }
            } catch (e) {
              existingArray = [{ content: rawExisting }];
            }
          }
        }

        existingContentArray = existingArray;
      }

      let updatedContentArray = existingContentArray;

      if (
        pageData &&
        pageData.page_type &&
        pageData.page_type !== "content" &&
        pageData.page_type !== "active_recall_content"
      ) {
        let itemsArray = processedContent;
        if (typeof itemsArray === "string") {
          try {
            const parsed = JSON.parse(itemsArray);
            if (Array.isArray(parsed)) {
              itemsArray = parsed;
            }
          } catch (e) {}
        }

        if (!Array.isArray(itemsArray)) {
          itemsArray = [itemsArray];
        }

        updatedContentArray = itemsArray.reduce((acc, item) => {
          if (!item || typeof item !== "object") return acc;
          const baseItem = {
            ...item,
            type: contentTypeForSave,
          };
          if (pageData && pageData.page_type === "quiz" && categories) {
            baseItem.categories = categories;
          }
          return insertContentWithSequenceManagement(
            acc,
            baseItem,
            insertionIndex,
          );
        }, existingContentArray);
      } else {
        newContentObject = {
          content: processedContent,
          type: contentTypeForSave,
          ...(pageData &&
            pageData.page_type === "quiz" &&
            categories && { categories: categories }),
        };

        updatedContentArray = insertContentWithSequenceManagement(
          existingContentArray,
          newContentObject,
          insertionIndex,
        );
      }

      let payload;
      let updatedPageData;
      let levelForRefetch =
        pageData && pageData.page_type === "test"
          ? (pageData.level || "").toLowerCase() || undefined
          : undefined;

      if (pageData && (pageData.page_type === "test" || pageData.page_type === "active_recall_test")) {
        let fieldName = "content";
        const level = (pageData.level || "").toLowerCase();
        if (level === "medium") fieldName = "medium_content";
        else if (level === "hard") fieldName = "hard_content";

        payload = { id: pageId, [fieldName]: updatedContentArray };
      } else {
        payload = { id: pageId, content: updatedContentArray };
      }
      switch (context) {
        case "course":
          await updatePageForCourse(payload);
          updatedPageData = await getPageForCourse(
            courseKey,
            pageId,
            levelForRefetch,
          );
          break;
        case "subject":
          await updatePageForSubject(payload);
          updatedPageData = await getPageForSubject(
            subjectKey,
            pageId,
            levelForRefetch,
          );
          break;
        case "topic":
          await updatePageForTopic(payload);
          updatedPageData = await getPageForTopic(
            topicKey,
            pageId,
            levelForRefetch,
          );
          break;
        case "subtopic":
          await updatePageForSubtopic(payload);
          updatedPageData = await getPageForSubtopic(
            subTopicKey,
            pageId,
            levelForRefetch,
          );
          break;
        default:
          throw new Error("Invalid context provided");
      }

      const resultWithLevel =
        levelForRefetch && updatedPageData
          ? { ...updatedPageData, level: levelForRefetch }
          : updatedPageData;

      onSave(resultWithLevel);
      handleClose();
    } catch (err) {
      setError("Failed to save manual content. Please try again.");
      console.error("Error saving manual content:", err);
    }
  };

  const detectContentType = (content) => {
    try {
      let parsedContent = content;
      if (typeof content === "string") {
        parsedContent = JSON.parse(content);
      }

      if (parsedContent.questions && Array.isArray(parsedContent.questions)) {
        const firstQuestion = parsedContent.questions[0];
        if (firstQuestion && firstQuestion.options) {
          return "mcq";
        }
        if (
          firstQuestion &&
          (firstQuestion.answer !== undefined ||
            firstQuestion.correct_answer !== undefined)
        ) {
          return "trueorfalse";
        }
      }

      return "content";
    } catch (e) {
      return "content";
    }
  };

  const handleClosePopup = () => {
    setContentType("trueorfalse");
    setPrompt("");
    setGeneratedContent("");
    setManualContent("");
    setError("");
    setIsLoading(false);
    handleClose();
  };

  const isNonEmpty = (value) => {
    return value !== undefined && value !== null && String(value).trim() !== "";
  };

  const isManualTabValid = () => {
    if (activeTab !== 1) return true;

    if (
      !pageData ||
      !pageData.page_type ||
      pageData.page_type === "content" ||
      pageData.page_type === "active_recall_content"
    ) {
      return isNonEmpty(manualContent);
    }

    if (contentType === "mcq") {
      if (
        !manualMCQContent ||
        !Array.isArray(manualMCQContent.questions) ||
        manualMCQContent.questions.length === 0
      ) {
        return false;
      }

      return manualMCQContent.questions.every((q) => {
        if (!isNonEmpty(q.question)) return false;

        const options = q.options || [];
        if (!options.length) return false;
        if (!options.every(isNonEmpty)) return false;

        const ci = q.correctIndex;
        if (ci === undefined || ci === null || ci < 0 || ci >= options.length) {
          return false;
        }

        if (!isNonEmpty(q.explanation)) return false;

        // q.hint is optional
        return true;
      });
    }

    if (contentType === "trueorfalse") {
      if (
        !manualTrueFalseContent ||
        !Array.isArray(manualTrueFalseContent.questions) ||
        manualTrueFalseContent.questions.length === 0
      ) {
        return false;
      }

      return manualTrueFalseContent.questions.every((q) => {
        if (!isNonEmpty(q.question || q.title || q.text)) return false;

        const answer =
          q.answer !== undefined
            ? q.answer
            : q.correct_answer !== undefined
              ? q.correct_answer
              : q.correct;
        if (!isNonEmpty(answer)) return false;

        if (!isNonEmpty(q.explanation)) return false;

        // q.hint is optional
        return true;
      });
    }

    if (contentType === "match") {
      if (
        !manualMatchContent ||
        !Array.isArray(manualMatchContent.pairs) ||
        manualMatchContent.pairs.length === 0
      ) {
        return false;
      }

      return manualMatchContent.pairs.every((pair) => {
        return isNonEmpty(pair.left) && isNonEmpty(pair.right);
      });
    }

    if (contentType === "fillups") {
      if (
        !manualFillUpsContent ||
        !Array.isArray(manualFillUpsContent.sentences) ||
        manualFillUpsContent.sentences.length === 0
      ) {
        return false;
      }

      return manualFillUpsContent.sentences.every((sentence) => {
        if (!isNonEmpty(sentence.sentence)) return false;

        const answers = sentence.answers || [];
        if (!answers.length) return false;
        if (!answers.every(isNonEmpty)) return false;

        if (!isNonEmpty(sentence.explanation)) return false;

        return true;
      });
    }

    if (contentType === "analysis") {
      if (!manualAnalysisContent) return false;

      if (!isNonEmpty(manualAnalysisContent.title)) return false;
      if (!isNonEmpty(manualAnalysisContent.content)) return false;

      return true;
    }

    return isNonEmpty(manualContent);
  };

  if (!open) {
    return null;
  }

  const isContentPage =
    pageData &&
    (pageData.page_type === "content" ||
      pageData.page_type === "active_recall_content");

  const isQuestionPageForSelect =
    pageData &&
    ["mcq", "match", "fillups", "trueorfalse", "analysis"].includes(
      pageData.page_type,
    );

  let generatedContentForViewer = generatedContent;
  if (pageData && (pageData.page_type === "test" || pageData.page_type === "active_recall_test") && generatedContent) {
    try {
      if (typeof generatedContent === "string") {
        const parsed = JSON.parse(generatedContent);
        if (
          parsed &&
          typeof parsed === "object" &&
          parsed.structured &&
          Array.isArray(parsed.structured.content)
        ) {
          generatedContentForViewer = parsed.structured.content;
        } else if (
          parsed &&
          typeof parsed === "object" &&
          Array.isArray(parsed.content)
        ) {
          generatedContentForViewer = parsed.content;
        } else if (
          parsed &&
          typeof parsed === "object" &&
          Array.isArray(parsed.questions)
        ) {
          generatedContentForViewer = parsed.questions;
        } else {
          generatedContentForViewer = parsed;
        }
      }
    } catch (e) {
      generatedContentForViewer = generatedContent;
    }
  }

  return (
    <div
      className={`content-generator-popup ${open ? "" : "hidden"} high-z-index`}
    >
      <div className="content-generator-dialog">
        <div className="content-generator-header">
          <h2 className="content-generator-title">
            {isReplaceMode
              ? pageData &&
                pageData.page_type &&
                pageData.page_type !== "content" &&
                pageData.page_type !== "active_recall_content"
                ? "Replace Question"
                : "Replace Content"
              : pageData &&
                pageData.page_type &&
                pageData.page_type !== "content" &&
                pageData.page_type !== "active_recall_content"
                ? "Generate Question"
                : "Generate Content"}
          </h2>
          <button
            className="content-generator-close"
            onClick={handleClosePopup}
          >
            ×
          </button>
        </div>

        <div className="content-generator-tabs">
          <div
            className={`content-generator-tab ${
              activeTab === 2 ? "active" : ""
            }`}
            onClick={() => setActiveTab(2)}
          >
            System Prompt
          </div>
          <div
            className={`content-generator-tab ${
              activeTab === 0 ? "active" : ""
            }`}
            onClick={() => setActiveTab(0)}
          >
            Generate with AI
          </div>
          <div
            className={`content-generator-tab ${
              activeTab === 1 ? "active" : ""
            }`}
            onClick={() => setActiveTab(1)}
          >
            Manual
          </div>
        </div>
        {activeTab !== 2 &&
          !isContentPage &&
          pageData &&
          pageData.page_type &&
          ((pageData.page_type !== "content" &&
            pageData.page_type !== "active_recall_content" &&
            pageData.page_type !== "active_recall_test" &&
            pageData.page_type !== "test") ||
            activeTab === 1 ||
            pageData.page_type === "quiz") && (
            <div className="content-generator-meta-row">
              <div className="content-generator-field">
                <label className="content-generator-label">Content Type</label>
                <Select
                  className="content-generator-select"
                  classNamePrefix="content-generator-select"
                  value={{
                    value: contentType,
                    label:
                      contentType.charAt(0).toUpperCase() +
                      contentType.slice(1),
                  }}
                  onChange={(selectedOption) =>
                    setContentType(selectedOption.value)
                  }
                  options={[
                    { value: "mcq", label: "MCQ" },
                    { value: "match", label: "Match" },
                    { value: "fillups", label: "Fill Ups" },
                    { value: "expand", label: "Expand" },
                    { value: "trueorfalse", label: "True or False" },
                    // { value: "analysis", label: "Analysis" },
                  ]}
                  isDisabled={isQuestionPageForSelect}
                />
              </div>

              {!(
                activeTab === 0 &&
                ["mcq", "trueorfalse", "fillups", "match", "analysis"].includes(
                  pageData.page_type,
                )
              ) && (
                <div className="content-generator-field">
                  <label className="content-generator-label">Categories</label>
                  <Select
                    className="content-generator-select"
                    classNamePrefix="content-generator-select"
                    value={
                      categories
                        ? {
                            value: categories,
                            label: categories
                              .replace("_", " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase()),
                          }
                        : null
                    }
                    onChange={(selectedOption) =>
                      setCategories(selectedOption ? selectedOption.value : "")
                    }
                    options={[
                      { value: "direct", label: "Direct" },
                      {
                        value: "critical_thinking",
                        label: "Critical Thinking",
                      },
                      { value: "reasoning", label: "Reasoning" },
                      { value: "application", label: "Application" },
                    ]}
                    placeholder="Select a category"
                  />
                </div>
              )}
            </div>
          )}

        <div className="content-generator-content">
          {activeTab === 0 && (
            <div className="content-generator-tab-content generate-ai-content active">
              <div className="input-section">
                <div className="prompt-section">
                  <textarea
                    className="prompt-textarea"
                    placeholder="Enter a prompt to generate content, e.g., 'React based content'"
                    value={prompt}
                    onChange={handlePromptChange}
                  />
                  {error && <div className="prompt-error">{error}</div>}

                  {pageData &&
                    pageData.page_type &&
                    pageData.page_type !== "content" &&
                    pageData.page_type !== "active_recall_content" && (
                      <div className="prompt-categories">
                        <div className="content-generator-label prompt-categories-label">
                          Categories
                        </div>
                        {testQuestionTypes
                          .filter((item) =>
                            pageData.page_type === "quiz"
                              ? item.key === contentType
                              : [
                                    "mcq",
                                    "trueorfalse",
                                    "fillups",
                                    "match",
                                    "analysis",
                                  ].includes(pageData.page_type)
                                ? item.key === pageData.page_type
                                : true,
                          )
                          .map((item) => (
                            <div key={item.key} className="prompt-category-row">
                              <div className="prompt-category-name">
                                {item.label}
                              </div>
                              {pageData.page_type !== "quiz" && (
                                <Switch
                                  size="small"
                                  checked={!!testSwitchState[item.key]}
                                  onChange={() => {
                                    setContentType(item.key);

                                    setTestSwitchState((prev) => ({
                                      ...prev,
                                      [item.key]: !prev[item.key],
                                    }));
                                  }}
                                />
                              )}
                              <input
                                type="number"
                                min="1"
                                className={`prompt-category-count-input ${
                                  testSwitchState[item.key] ||
                                  pageData.page_type === "quiz"
                                    ? "visible"
                                    : "hidden"
                                }`}
                                placeholder="No .of Count"
                                value={(
                                  testQuestionCounts[item.key] ?? ""
                                ).toString()}
                                onChange={(e) => {
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    "",
                                  );
                                  setTestQuestionCounts((prev) => ({
                                    ...prev,
                                    [item.key]: value,
                                  }));
                                }}
                              />
                            </div>
                          ))}
                      </div>
                    )}
                </div>

                {websocketStatus === "completed" &&
                  generatedContent &&
                  pageData &&
                  (pageData.page_type === "content" ||
                    pageData.page_type === "active_recall_content") && (
                    <div className="generated-content-viewer">
                      <MDEditor
                        value={normalizeMarkdownValue(generatedContent)}
                        onChange={setGeneratedContent}
                        preview="live"
                        previewOptions={mdPreviewOptions}
                        height={400}
                      />
                    </div>
                  )}
              </div>

              <div className="content-display">
                {websocketStatus === "completed" &&
                pageData &&
                (pageData.page_type === "content" ||
                  pageData.page_type === "active_recall_content") ? (
                  <MDEditor
                    value={normalizeMarkdownValue(generatedContent)}
                    onChange={setGeneratedContent}
                    preview="live"
                    previewOptions={mdPreviewOptions}
                    height={800}
                  />
                ) : websocketStatus === "completed" &&
                  pageData &&
                  pageData.page_type !== "content" &&
                  pageData.page_type !== "active_recall_content" &&
                  generatedContent ? (
                  <>
                    {(pageData.page_type === "test" || pageData.page_type === "active_recall_test") &&
                    Array.isArray(generatedContentForViewer) ? (
                      generatedContentForViewer.map((item, index) => {
                        let itemType = "content";
                        let itemContent = item;

                        if (item && typeof item === "object") {
                          if (item.type) {
                            itemType = item.type;
                          }
                          if (item.content) {
                            itemContent = item.content;
                          }
                        }

                        switch (itemType) {
                          case "mcq":
                            return (
                              <MCQViewer
                                key={`mcq-${index}`}
                                questionData={itemContent}
                              />
                            );
                          case "trueorfalse":
                            return (
                              <TrueFalseViewer
                                key={`tf-${index}`}
                                questionData={itemContent}
                              />
                            );
                          case "match":
                            return (
                              <MatchViewer
                                key={`match-${index}`}
                                questionData={itemContent}
                              />
                            );
                          case "fillups":
                            return (
                              <FillUpsViewer
                                key={`fillups-${index}`}
                                questionData={itemContent}
                              />
                            );
                          case "analysis":
                            return (
                              <AnalysisComponent
                                key={`analysis-${index}`}
                                data={itemContent}
                              />
                            );
                          default:
                            return (
                              <MDEditor
                                key={`md-${index}`}
                                value={normalizeMarkdownValue(itemContent)}
                                onChange={setGeneratedContent}
                                preview="live"
                                previewOptions={mdPreviewOptions}
                                height={400}
                              />
                            );
                        }
                      })
                    ) : (() => {
                      // Extract all questions into a flat array for rendering
                      let questionsToRender = [];
                      if (
                        generatedContentForViewer &&
                        typeof generatedContentForViewer === "object" &&
                        !Array.isArray(generatedContentForViewer) &&
                        Array.isArray(generatedContentForViewer.questions)
                      ) {
                        questionsToRender = generatedContentForViewer.questions;
                      } else if (Array.isArray(generatedContentForViewer)) {
                        questionsToRender = generatedContentForViewer;
                      }

                      // If multiple questions, render each with its own viewer
                      if (questionsToRender.length > 1) {
                        return questionsToRender.map((item, index) => {
                          const itemType = item?.type || contentType;
                          const itemContent = item?.content || item;

                          switch (itemType) {
                            case "mcq":
                              return (
                                <MCQViewer
                                  key={`mcq-${index}`}
                                  questionData={itemContent}
                                />
                              );
                            case "trueorfalse":
                              return (
                                <TrueFalseViewer
                                  key={`tf-${index}`}
                                  questionData={itemContent}
                                />
                              );
                            case "match":
                              return (
                                <MatchViewer
                                  key={`match-${index}`}
                                  questionData={itemContent}
                                />
                              );
                            case "fillups":
                              return (
                                <FillUpsViewer
                                  key={`fillups-${index}`}
                                  questionData={itemContent}
                                />
                              );
                            case "analysis":
                              return (
                                <AnalysisComponent
                                  key={`analysis-${index}`}
                                  data={itemContent}
                                />
                              );
                            default:
                              return (
                                <MDEditor
                                  key={`md-${index}`}
                                  value={normalizeMarkdownValue(itemContent)}
                                  onChange={setGeneratedContent}
                                  preview="live"
                                  previewOptions={mdPreviewOptions}
                                  height={400}
                                />
                              );
                          }
                        });
                      }

                      // Single question or direct content — use original single-viewer logic
                      return contentType === "mcq" ? (
                        <MCQViewer questionData={generatedContentForViewer} />
                      ) : contentType === "trueorfalse" ? (
                        <TrueFalseViewer
                          questionData={generatedContentForViewer}
                        />
                      ) : contentType === "match" ? (
                        <MatchViewer questionData={generatedContentForViewer} />
                      ) : contentType === "fillups" ? (
                        <FillUpsViewer questionData={generatedContentForViewer} />
                      ) : contentType === "analysis" ? (
                        <AnalysisComponent data={generatedContentForViewer} />
                      ) : (
                        <MDEditor
                          value={normalizeMarkdownValue(generatedContent)}
                          onChange={setGeneratedContent}
                          preview="live"
                          previewOptions={mdPreviewOptions}
                          height={400}
                        />
                      );
                    })()}
                  </>
                ) : pageData &&
                  pageData.page_type !== "content" &&
                  pageData.page_type !== "active_recall_content" &&
                  !generatedContent ? (
                  <div className="content-preview-placeholder"></div>
                ) : (
                  <div className="content-preview-placeholder"></div>
                )}
              </div>
            </div>
          )}
          {activeTab === 1 && (
            <div className="content-generator-tab-content manual-content active">
              <div className="input-section">
                {pageData &&
                pageData.page_type === "content" &&
                pageData.page_type === "active_recall_content" ? (
                  <div className="manual-text-content">
                    <MDEditor
                      value={normalizeMarkdownValue(manualContent)}
                      onChange={setManualContent}
                      preview="live"
                      previewOptions={mdPreviewOptions}
                      height={400}
                    />
                  </div>
                ) : pageData &&
                  pageData.page_type !== "content" &&
                  pageData.page_type !== "active_recall_content" &&
                  contentType === "mcq" ? (
                  <div className="manual-mcq-content">
                    <h3 className="mcq-title">MCQ Questions</h3>
                    {manualMCQContent.questions.map((q, index) => (
                      <div key={index} className="mcq-question">
                        <input
                          className="question-input"
                          placeholder={`Question ${index + 1}`}
                          value={q.question || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setManualMCQContent((prev) => ({
                              ...prev,
                              questions: prev.questions.map((qq, i) =>
                                i === index ? { ...qq, question: val } : qq,
                              ),
                            }));
                          }}
                        />
                        {(q.options || []).map((option, optIndex) => {
                          let optionText = "";
                          if (typeof option === "object" && option !== null) {
                            if (Object.keys(option).length === 1) {
                              const firstValue = Object.values(option)[0];
                              optionText =
                                typeof firstValue === "string"
                                  ? firstValue
                                  : JSON.stringify(firstValue);
                            } else {
                              optionText =
                                option.text ||
                                option.option ||
                                option.value ||
                                option.answer ||
                                JSON.stringify(option);
                            }
                          } else {
                            optionText = option;
                          }
                          return (
                            <div key={optIndex} className="option-row">
                              <input
                                type="radio"
                                className="option-radio"
                                checked={q.correctIndex === optIndex}
                                onChange={() => {
                                  setManualMCQContent((prev) => ({
                                    ...prev,
                                    questions: prev.questions.map((qq, i) =>
                                      i === index
                                        ? { ...qq, correctIndex: optIndex }
                                        : qq,
                                    ),
                                  }));
                                }}
                              />
                              <input
                                className="option-input"
                                placeholder={`Option ${String.fromCharCode(
                                  65 + optIndex,
                                )}`}
                                value={optionText}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setManualMCQContent((prev) => ({
                                    ...prev,
                                    questions: prev.questions.map((qq, i) => {
                                      if (i !== index) return qq;
                                      const opts = [...(qq.options || [])];
                                      opts[optIndex] = val;
                                      return { ...qq, options: opts };
                                    }),
                                  }));
                                }}
                              />
                              <button
                                className="option-delete-btn"
                                onClick={() => {
                                  setManualMCQContent((prev) => ({
                                    ...prev,
                                    questions: prev.questions.map((qq, i) => {
                                      if (i !== index) return qq;
                                      const opts = (qq.options || []).filter(
                                        (_, oi) => oi !== optIndex,
                                      );
                                      let newCorrectIndex = qq.correctIndex;
                                      if (optIndex < qq.correctIndex) {
                                        newCorrectIndex = Math.max(
                                          0,
                                          qq.correctIndex - 1,
                                        );
                                      } else if (optIndex === qq.correctIndex) {
                                        newCorrectIndex = Math.min(
                                          opts.length - 1,
                                          qq.correctIndex,
                                        );
                                      }
                                      return {
                                        ...qq,
                                        options: opts,
                                        correctIndex: newCorrectIndex,
                                      };
                                    }),
                                  }));
                                }}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                        <button
                          className="add-option-btn"
                          onClick={() => {
                            setManualMCQContent((prev) => ({
                              ...prev,
                              questions: prev.questions.map((qq, i) =>
                                i === index
                                  ? {
                                      ...qq,
                                      options: [...(qq.options || []), ""],
                                    }
                                  : qq,
                              ),
                            }));
                          }}
                        >
                          + Add Option
                        </button>
                        <textarea
                          className="explanation-textarea"
                          placeholder="Explanation"
                          value={q.explanation || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setManualMCQContent((prev) => ({
                              ...prev,
                              questions: prev.questions.map((qq, i) =>
                                i === index ? { ...qq, explanation: val } : qq,
                              ),
                            }));
                          }}
                        />
                        <textarea
                          className="explanation-textarea"
                          placeholder="Hint (optional)"
                          value={q.hint || ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setManualMCQContent((prev) => ({
                              ...prev,
                              questions: prev.questions.map((qq, i) =>
                                i === index ? { ...qq, hint: val } : qq,
                              ),
                            }));
                          }}
                          style={{ marginTop: 8 }}
                        />
                        {manualMCQContent.questions.length > 1 && (
                          <div style={{ marginTop: 16 }}>
                            <button
                              className="remove-question-btn"
                              onClick={() => handleRemoveMCQQuestion(index)}
                            >
                              Remove Question
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    <button
                      className="add-question-btn"
                      onClick={handleAddMCQQuestion}
                    >
                      + Add Question
                    </button>
                  </div>
                ) : pageData &&
                  pageData.page_type !== "content" &&
                  pageData.page_type !== "active_recall_content" &&
                  contentType === "trueorfalse" ? (
                  <div className="manual-truefalse-content">
                    <h3 className="tf-title">True/False Questions</h3>
                    {manualTrueFalseContent.questions.map((q, index) => {
                      const correctAnswer = normalizeAnswer(
                        q.answer || q.correct_answer || q.correct || "true",
                      );
                      const normalizedCorrectAnswer =
                        normalizeAnswer(correctAnswer);

                      return (
                        <div key={index} className="tf-question">
                          <input
                            className="question-input"
                            placeholder={`Question ${index + 1}`}
                            value={q.question || q.title || q.text || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setManualTrueFalseContent((prev) => ({
                                ...prev,
                                questions: prev.questions.map((qq, i) =>
                                  i === index ? { ...qq, question: val } : qq,
                                ),
                              }));
                            }}
                          />
                          <div className="tf-answer">
                            <div
                              className={`tf-option ${
                                normalizedCorrectAnswer === "true"
                                  ? "correct"
                                  : ""
                              }`}
                              onClick={() => {
                                setManualTrueFalseContent((prev) => ({
                                  ...prev,
                                  questions: prev.questions.map((qq, i) =>
                                    i === index
                                      ? {
                                          ...qq,
                                          answer: "true",
                                          correct_answer: "true",
                                        }
                                      : qq,
                                  ),
                                }));
                              }}
                            >
                              <input
                                type="radio"
                                className="tf-radio"
                                checked={normalizedCorrectAnswer === "true"}
                                onChange={() => {}}
                              />
                              <span>True</span>
                            </div>
                            <div
                              className={`tf-option ${
                                normalizedCorrectAnswer === "false"
                                  ? "correct"
                                  : ""
                              }`}
                              onClick={() => {
                                setManualTrueFalseContent((prev) => ({
                                  ...prev,
                                  questions: prev.questions.map((qq, i) =>
                                    i === index
                                      ? {
                                          ...qq,
                                          answer: "false",
                                          correct_answer: "false",
                                        }
                                      : qq,
                                  ),
                                }));
                              }}
                            >
                              <input
                                type="radio"
                                className="tf-radio"
                                checked={normalizedCorrectAnswer === "false"}
                                onChange={() => {}}
                              />
                              <span>False</span>
                            </div>
                          </div>
                          <textarea
                            className="explanation-textarea"
                            placeholder="Explanation"
                            value={q.explanation || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setManualTrueFalseContent((prev) => ({
                                ...prev,
                                questions: prev.questions.map((qq, i) =>
                                  i === index
                                    ? { ...qq, explanation: val }
                                    : qq,
                                ),
                              }));
                            }}
                          />
                          <textarea
                            className="explanation-textarea"
                            placeholder="Hint (optional)"
                            value={q.hint || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setManualTrueFalseContent((prev) => ({
                                ...prev,
                                questions: prev.questions.map((qq, i) =>
                                  i === index ? { ...qq, hint: val } : qq,
                                ),
                              }));
                            }}
                            style={{ marginTop: 8 }}
                          />
                          {manualTrueFalseContent.questions.length > 1 && (
                            <div style={{ marginTop: 16 }}>
                              <button
                                className="remove-question-btn"
                                onClick={() =>
                                  handleRemoveTrueFalseQuestion(index)
                                }
                              >
                                Remove Question
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <button
                      className="add-question-btn"
                      onClick={handleAddTrueFalseQuestion}
                    >
                      + Add Question
                    </button>
                  </div>
                ) : pageData &&
                  pageData.page_type !== "content" &&
                  pageData.page_type !== "active_recall_content" &&
                  contentType === "match" ? (
                  <div className="manual-match-content">
                    <h3 className="match-title">Match Pairs</h3>
                    {manualMatchContent.pairs.map((pair, index) => (
                      <div key={index} className="match-pair">
                        <input
                          className="match-left-input"
                          placeholder="Left side"
                          value={pair.left}
                          onChange={(e) =>
                            handleMatchPairChange(index, "left", e.target.value)
                          }
                        />
                        <input
                          className="match-right-input"
                          placeholder="Right side"
                          value={pair.right}
                          onChange={(e) =>
                            handleMatchPairChange(
                              index,
                              "right",
                              e.target.value,
                            )
                          }
                        />
                        {manualMatchContent.pairs.length > 1 && (
                          <button
                            className="remove-pair-btn"
                            onClick={() => handleRemoveMatchPair(index)}
                          >
                            Remove Pair
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="add-pair-btn"
                      onClick={handleAddMatchPair}
                    >
                      + Add Pair
                    </button>
                  </div>
                ) : pageData &&
                  pageData.page_type !== "content" &&
                  pageData.page_type !== "active_recall_content" &&
                  contentType === "fillups" ? (
                  <div className="manual-fillups-content">
                    <h3 className="fillups-title">Fill in the Blanks</h3>
                    {manualFillUpsContent.sentences.map((sentence, index) => (
                      <div key={index} className="fillups-sentence">
                        <textarea
                          className="sentence-textarea"
                          placeholder="Sentence with blanks represented by underscores, e.g., 'The capital of France is ____.'"
                          value={sentence.sentence}
                          onChange={(e) =>
                            handleFillUpsSentenceChange(
                              index,
                              "sentence",
                              e.target.value,
                            )
                          }
                        />
                        <div className="answers-section">
                          <h4>Answers:</h4>
                          {sentence.answers.map((answer, ansIndex) => (
                            <div key={ansIndex} className="answer-input">
                              <input
                                type="text"
                                placeholder={`Answer ${ansIndex + 1}`}
                                value={answer}
                                onChange={(e) =>
                                  handleFillUpsAnswerChange(
                                    index,
                                    ansIndex,
                                    e.target.value,
                                  )
                                }
                              />
                              {sentence.answers.length > 1 && (
                                <button
                                  className="remove-answer-btn"
                                  onClick={() =>
                                    handleRemoveFillUpsAnswer(index, ansIndex)
                                  }
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            className="add-answer-btn"
                            onClick={() => handleAddFillUpsAnswer(index)}
                          >
                            + Add Answer
                          </button>
                        </div>
                        <div
                          className="form-group"
                          style={{ marginTop: "16px" }}
                        >
                          <label className="form-label">Explanation</label>
                          <textarea
                            className="form-control explanation-textarea"
                            placeholder="Explanation"
                            value={sentence.explanation || ""}
                            onChange={(e) =>
                              handleFillUpsSentenceChange(
                                index,
                                "explanation",
                                e.target.value,
                              )
                            }
                            rows={3}
                          />
                        </div>
                        <div
                          className="form-group"
                          style={{ marginTop: "16px" }}
                        >
                          <label className="form-label">Hint (optional)</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Hint"
                            value={sentence.hint || ""}
                            onChange={(e) =>
                              handleFillUpsSentenceChange(
                                index,
                                "hint",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                        {manualFillUpsContent.sentences.length > 1 && (
                          <button
                            className="remove-sentence-btn"
                            onClick={() => handleRemoveFillUpsSentence(index)}
                          >
                            Remove Sentence
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      className="add-sentence-btn"
                      onClick={handleAddFillUpsSentence}
                    >
                      + Add Sentence
                    </button>
                  </div>
                ) : pageData &&
                  pageData.page_type !== "content" &&
                  pageData.page_type !== "active_recall_content" &&
                  contentType === "analysis" ? (
                  <div className="manual-analysis-content">
                    <h3 className="analysis-title">Analysis Content</h3>
                    {/* <input
                      className="analysis-title-input"
                      placeholder="Analysis Title"
                      value={manualAnalysisContent.title}
                      onChange={(e) =>
                        handleAnalysisTitleChange(e.target.value)
                      }
                    />
                    <MDEditor
                      value={manualAnalysisContent.content}
                      onChange={handleAnalysisContentChange}
                      preview="live"
                      previewOptions={mdPreviewOptions}
                      height={300}
                    />
                    <textarea
                      className="explanation-textarea"
                      placeholder="Hint (optional)"
                      value={manualAnalysisContent.hint || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setManualAnalysisContent((prev) => ({
                          ...prev,
                          hint: val,
                        }));
                      }}
                      style={{ marginTop: 8 }}
                    /> */}
                    <div className="tf-question">
                      <input
                        className="question-input"
                        placeholder={`Question`}
                        value={manualAnalysisContent.title || ""}
                        onChange={(e) =>
                          handleAnalysisTitleChange(e.target.value)
                        }
                      />
                      <textarea
                        className="explanation-textarea"
                        placeholder="Explanation"
                        value={manualAnalysisContent.content || ""}
                        onChange={(e) =>
                          handleAnalysisContentChange(e.target.value)
                        }
                      />
                      <textarea
                        className="explanation-textarea"
                        placeholder="Hint (optional)"
                        value={manualAnalysisContent.hint || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setManualAnalysisContent((prev) => ({
                            ...prev,
                            hint: val,
                          }));
                        }}
                        style={{ marginTop: 8 }}
                      />
                      {manualTrueFalseContent.questions.length > 1 && (
                        <div style={{ marginTop: 16 }}>
                          <button
                            className="remove-question-btn"
                            onClick={() => handleRemoveTrueFalseQuestion(1)}
                          >
                            Remove Question
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="manual-text-content">
                    <MDEditor
                      value={normalizeMarkdownValue(manualContent || "")}
                      onChange={setManualContent}
                      preview="live"
                      previewOptions={mdPreviewOptions}
                      height={400}
                    />
                  </div>
                )}
              </div>

              {!(
                pageData &&
                pageData.page_type === "content" &&
                pageData.page_type === "active_recall_content" &&
                activeTab === 1
              ) &&
                contentType !== "mcq" && (
                  <div className="preview-section">
                    <h3 className="preview-title">Content Preview</h3>
                    <div className="content-preview">
                      {pageData && pageData.page_type === "content" ? (
                        <div className="content-preview-placeholder">
                          <p>Editor is shown in the input section</p>
                        </div>
                      ) : pageData &&
                        pageData.page_type !== "content" &&
                        ((activeTab === 0 && generatedContent) ||
                          activeTab === 1) ? (
                        <>
                          {contentType === "trueorfalse" ? (
                            <TrueFalseViewer
                              questionData={
                                activeTab === 0
                                  ? generatedContent
                                  : manualTrueFalseContent
                              }
                            />
                          ) : contentType === "match" ? (
                            <MatchViewer
                              questionData={
                                activeTab === 0
                                  ? generatedContent
                                  : {
                                      left: (
                                        manualMatchContent.pairs || []
                                      ).map((p) => p.left),
                                      right: (
                                        manualMatchContent.pairs || []
                                      ).map((p) => p.right),
                                    }
                              }
                            />
                          ) : contentType === "fillups" ? (
                            <FillUpsViewer
                              questionData={
                                activeTab === 0
                                  ? generatedContent
                                  : {
                                      questions: (
                                        manualFillUpsContent.sentences || []
                                      ).map((s) => ({
                                        question: s.sentence || "",
                                        answer:
                                          (Array.isArray(s.answers) &&
                                            s.answers[0]) ||
                                          "",
                                      })),
                                    }
                              }
                            />
                          ) : contentType === "analysis" ? (
                            <AnalysisComponent
                              data={
                                activeTab === 0
                                  ? generatedContent
                                  : {
                                      questions: [
                                        {
                                          question:
                                            manualAnalysisContent.title || "",
                                          explanation:
                                            manualAnalysisContent.content || "",
                                        },
                                      ],
                                    }
                              }
                            />
                          ) : (
                            <MDEditor
                              value={
                                typeof manualContent === "string"
                                  ? manualContent
                                  : JSON.stringify(manualContent, null, 2)
                              }
                              onChange={setManualContent}
                              preview="live"
                              previewOptions={mdPreviewOptions}
                              height={400}
                            />
                          )}
                        </>
                      ) : pageData &&
                        pageData.page_type !== "content" &&
                        pageData.page_type !== "active_recall_content" &&
                        activeTab === 0 &&
                        !generatedContent ? (
                        <div className="content-preview-placeholder">
                          <p>Generate content to see preview</p>
                        </div>
                      ) : (
                        <div className="content-preview-placeholder">
                          <p>
                            {activeTab === 0
                              ? "Select content type and generate content"
                              : "Enter content to see preview"}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
          {activeTab === 2 && (
            <div className="content-generator-tab-content system-prompt-content active">
              {systemPromptsLoading ? (
                <div className="system-prompts-loading">Loading system prompts...</div>
              ) : systemPromptsError ? (
                <div className="system-prompts-error">{systemPromptsError}</div>
              ) : Object.keys(systemPrompts).length === 0 ? (
                <div className="system-prompts-empty">No default system prompts found.</div>
              ) : (
                <div className="system-prompts-list">
                  {Object.entries(systemPrompts).map(([type, promptObj]) => (
                    <div key={type} className="system-prompt-item">
                      <div className="system-prompt-item-header">
                        <span className="system-prompt-type-label">
                          {type === "trueorfalse" ? "True or False" : type === "mcq" ? "MCQ" : type === "fillups" ? "Fill Ups" : type.charAt(0).toUpperCase() + type.slice(1)} System Prompt ({promptObj.name})
                        </span>
                        <div className="system-prompt-actions">
                          {systemPromptSaveStatus[type] && (
                            <span className={`system-prompt-status-msg ${systemPromptSaveStatus[type].includes("Failed") ? "error" : "success"}`}>
                              {systemPromptSaveStatus[type]}
                            </span>
                          )}
                          {isPromptEditing[type] && !systemPromptSaving[type] && (
                            <button
                              type="button"
                              className="action-btn small"
                              onClick={() => handleCancelSystemPromptEdit(type)}
                              style={{ marginRight: "8px" }}
                            >
                              Cancel
                            </button>
                          )}
                          <button
                            type="button"
                            className="action-btn primary small"
                            onClick={() => {
                              if (isPromptEditing[type]) {
                                handleSaveSystemPrompt(type);
                              } else {
                                setIsPromptEditing((prev) => ({
                                  ...prev,
                                  [type]: true,
                                }));
                              }
                            }}
                            disabled={systemPromptSaving[type]}
                          >
                            {systemPromptSaving[type]
                              ? "Saving..."
                              : isPromptEditing[type]
                              ? "Save"
                              : "Edit"}
                          </button>
                        </div>
                      </div>
                      <textarea
                        className="system-prompt-textarea"
                        value={promptObj.prompt || ""}
                        onChange={(e) => handleSystemPromptTextChange(type, e.target.value)}
                        placeholder={`Enter system prompt for ${type}...`}
                        readOnly={!isPromptEditing[type]}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {activeTab !== 2 && (
          <div className="content-generator-actions">
            {activeTab === 0 && (
              <>
                <button
                  type="button"
                  className="action-btn primary"
                  onClick={handleOpenContext}
                >
                  Context
                </button>
                <button
                  className="action-btn primary"
                  onClick={handleGenerate}
                  disabled={websocketStatus === "processing"}
                >
                  {websocketStatus === "processing" ? (
                    <>
                      <span className="loading-text">Generating...</span>
                    </>
                  ) : (
                    "Generate"
                  )}
                </button>
                {generatedContent && (
                  <>
                    <button
                      className={`action-btn primary  ${
                        websocketStatus === "processing"
                          ? "websocketStatusDisable"
                          : ""
                      }`}
                      disabled={websocketStatus === "processing"}
                      onClick={handleSave}
                    >
                      {isReplaceMode ? "Replace Content" : "Save Content"}
                    </button>
                    <button className="action-btn" onClick={handleClosePopup}>
                      Cancel
                    </button>
                  </>
                )}
              </>
            )}
            {activeTab === 1 && (
              <button
                className={`action-btn primary ${
                  !isManualTabValid() ? "action-btn-disabled" : ""
                }`}
                onClick={handleSaveManualContent}
                disabled={!isManualTabValid()}
              >
                {isReplaceMode ? "Replace Content" : "Save Content"}
              </button>
            )}
          </div>
        )}
      </div>

      {isContextOpen && (
        <ContextPopup
          open={isContextOpen}
          onClose={handleCloseContext}
          materials={contextMaterials}
          loading={contextLoading}
          error={contextError}
          onExtract={handleContextExtract}
          pageSelections={contextPageSelections}
        />
      )}

      {contextSuccessOpen && (
        <SuccessPopup
          open={contextSuccessOpen}
          onClose={() => {
            setContextSuccessOpen(false);
            setIsContextOpen(false);
          }}
          message="Context extracted and saved successfully!"
          duration={2500}
        />
      )}
    </div>
  );
};

export default ContentGeneratorPopup;
